import { compact } from 'lodash-es';
import { getTitleValidator } from 'validators';
import { addMonths, isAfter, isBefore, startOfDay, addYears } from 'date-fns';
import * as selectors from "./selectors";
import { fromDateStampObject } from "../time";
import { DiscountExpirationType } from "../LimitedTimeDiscountOfferModel";
import { serviceBillingModel, serviceCostModel, serviceModel } from '..';
export function validateDayOfMonth(service) {
  const billingDayOfMonth = selectors.billingDayOfMonth(service);
  const isBilledUpfront = selectors.isBilledUpfront(service);
  if (!billingDayOfMonth || isBilledUpfront == null) return [];
  return validateBillingDayOfMonth(billingDayOfMonth, isBilledUpfront) ? ['billed upfront on last day of month'] : [];
}
export function validateMaxCharges(service) {
  const maxCharges = selectors.maxChargesLimit(service);
  const isMaxChargesEnabled = selectors.maxChargesEnabled(service);
  if (isMaxChargesEnabled) {
    if (maxCharges == null) {
      return ['max charges is empty'];
    } else if (maxCharges < 1 || maxCharges > 99) {
      return ['max charges not in valid range (1 >= maxCharges <= 99)'];
    }
  }
  return [];
}
export const roundPricesErrorMsg = 'Round down may reduce the rate below the original.';
export const increaseMethodErrorMsg = 'anniversary option is not allowed';
export function validatePriceIncrease(service, opts) {
  const priceIncrease = selectors.priceIncrease(service);
  const increaseOn = priceIncrease.increaseOn();
  return compact([priceIncrease.exists() && isInvalidPercentIncrease(priceIncrease.percentIncrease()) && 'amount must be positive', isInvalidRoundPrices(service) && roundPricesErrorMsg, priceIncrease.exists() && isInvalidIncreaseMethod(increaseOn, opts.relationshipEffectiveDate) && increaseMethodErrorMsg, priceIncrease.exists() && increaseOn && opts.relationshipEffectiveDate && isBefore(increaseOn, opts.relationshipEffectiveDate) && 'date must be after agreement effective date']);
}
export function isInvalidIncreaseMethod(increaseOn, relationshipEffectiveDate) {
  return !increaseOn && !!relationshipEffectiveDate;
}
export function isInvalidPercentIncrease(percentIncrease) {
  if (typeof percentIncrease === 'string') return !percentIncrease || +percentIncrease <= 0;
  return percentIncrease.isNegative() || percentIncrease.isZero();
}
export function isInvalidRoundPrices(service) {
  const nextYearPrice = selectors.nextYearPrice(service);
  return nextYearPrice != null && selectors.finalPrice(service, 1).gt(nextYearPrice);
}
export function validateName(name, nameBlacklist, isBundle) {
  const label = isBundle ? 'Bundle name' : 'Service name';
  const trimmed = name.trim();
  if (!trimmed) return "".concat(label, " is required");
  if (nameBlacklist && nameBlacklist.includes(trimmed)) return 'You already use this service name';
  const validation = getTitleValidator(label)(name);
  if (validation !== true) return typeof validation === 'string' ? validation : "".concat(label, " is invalid");
  return undefined;
}

/** Will return true if there's an error. */
export const validateBillingDayOfMonth = (billingDayOfMonth, isBilledUpfront) => billingDayOfMonth.isLast() && isBilledUpfront;
export function validateIntegratedClass(service) {
  const integratedClassId = selectors.integratedClassId(service);
  if (integratedClassId === '') return ['Please select a class'];
  return [];
}
export function showMaxChargesIndicator(service) {
  return selectors.maxChargesEnabled(service) && validateMaxCharges(service).length === 0;
}
export function validateLimitedTimeDiscountOffer(service, now) {
  const limitedTimeDiscountOffer = selectors.limitedTimeDiscountOffer(service);
  if (!limitedTimeDiscountOffer) return '';
  let error = '';
  const expirationType = selectors.limitedTimeDiscountOfferExpirationType(service);
  if (limitedTimeDiscountOffer.expiration.date) {
    const expirationDate = fromDateStampObject(limitedTimeDiscountOffer.expiration.date);
    if (isBefore(expirationDate, startOfDay(now))) error = 'The deadline for accepting the proposal must be set to a future date';
  }
  if (expirationType === DiscountExpirationType.Days) {
    const days = serviceModel.limitedTimeDiscountOfferExpirationDays(service);
    if (days && days > 90) error = 'Limited time discount offer must be less than 90 days';

    //im checking here for undefined because if days is 0, checking for 'days && ...' will be false
    if (days !== undefined && days < 1) error = 'Limited time discount offer must be at least 1 day';
  } else if (expirationType === DiscountExpirationType.Date) {
    const expirationDate = serviceModel.limitedTimeDiscountOfferExpirationDate(service);
    if (expirationDate && isAfter(fromDateStampObject(expirationDate), addMonths(now, 4))) error = 'Limited time discount offer must be within 4 months';
  }
  return error;
}
export function validateDiscountPeriodExpiration(service, now, effectiveDate) {
  const cost = serviceModel.cost(service);
  const discountPeriodExpiration = cost.discount ? serviceCostModel.discountPeriodExpiration({
    discount: cost.discount
  }) : undefined;
  if (!discountPeriodExpiration) return '';
  let error = '';
  const expirationType = discountPeriodExpiration.type();
  if (expirationType === 'date') {
    const expirationDate = discountPeriodExpiration.date();
    if (expirationDate) {
      if (isBefore(expirationDate, startOfDay(now))) error = 'The automatic expiration date of the discount must be set in the future';
      if (isAfter(expirationDate, addYears(now, 4))) error = 'The automatic expiration can be set up to 4 years in the future';
      if (effectiveDate && isBefore(expirationDate, effectiveDate)) error = "The automatic discount expiration date must be after the agreement's effective date.";
    }
  } else if (expirationType === 'period') {
    const periodType = discountPeriodExpiration.period();
    if (!periodType) return 'Invalid period type';
    const amount = discountPeriodExpiration.amount();
    if (amount !== undefined && amount < 1) error = 'Discount expiration amount must be at least 1';
    discountPeriodExpiration.matchPeriod(periodType, {
      days: () => {
        if (amount && amount > 90) error = 'The automatic expiration can be set up to 90 days in the future';
      },
      weeks: () => {
        if (amount && amount > 12) error = 'The automatic expiration can be set up to 12 weeks in the future';
      },
      months: () => {
        if (amount && amount > 36) error = 'The automatic expiration can be set up to 36 months in the future';
      },
      years: () => {
        if (amount && amount > 4) error = 'The automatic expiration can be set up to 4 years in the future';
      }
    });
  }
  return error;
}
export function validatePreapprovedAmount(service) {
  var _serviceModel$changes;
  // only validating if advanced settings is controlling the preapproved amount
  // because if its from the advanced settings, there is an input field that needs to be validated
  // and if its not from the advanced settings, service builder handles the validation
  const cost = ((_serviceModel$changes = serviceModel.changes(service)) === null || _serviceModel$changes === void 0 ? void 0 : _serviceModel$changes.cost) || service.cost;
  const isControlledByAdvancedSettings = serviceCostModel.preapprovedControlledByAdvancedSettings(cost);
  if (!isControlledByAdvancedSettings) return '';
  const unitCap = serviceCostModel.unitCap(cost);
  if (!unitCap || unitCap.lessThanOrEqualTo(0)) return 'Preapproved amount must be greater than 0';
  return '';
}
export let BundleError = /*#__PURE__*/function (BundleError) {
  BundleError["EmptyIncludedServices"] = "emptyIncludedServices";
  return BundleError;
}({});
export function validateBundles(service) {
  if (!service.bundle) return [];
  return validateBundleIncludedServices(service.bundle.services);
}
export function validateStartBillingAt(service, opts) {
  if (opts.isTemplate) return [];
  const relationshipEffectiveDate = opts.relationshipEffectiveDate;
  const today = startOfDay(opts.now);
  const startBillingAt = selectors.startBillingAt(service);
  const oneYearAgo = addYears(today, -1);
  if (!startBillingAt) {
    return [];
  }
  if (!relationshipEffectiveDate) {
    return ['Start billing on a specific date isn’t available if the agreement effective date is “on acceptance.”'];
  }
  const relationshipEffectiveDateToday = startOfDay(relationshipEffectiveDate);
  const startBillingAtToday = startOfDay(startBillingAt);
  if (isBefore(startBillingAtToday, relationshipEffectiveDateToday)) {
    return ["The start date for automatic billing cannot be set earlier than the agreement's effective date"];
  }
  if (isBefore(startBillingAtToday, oneYearAgo)) {
    return ['Date must be within the past year'];
  }
  return [];
}
export function validateBundleIncludedServices(includedServices) {
  return includedServices.length === 0 ? [BundleError.EmptyIncludedServices] : [];
}
export let OneTimeInStagesError = /*#__PURE__*/function (OneTimeInStagesError) {
  OneTimeInStagesError["InvalidStages"] = "billInStagesInvalidStages";
  OneTimeInStagesError["NonPercentageDiscount"] = "billInStagesNonPercentageDiscount";
  return OneTimeInStagesError;
}({});
export function validateOneTimeInStages(service) {
  const oneTimeInStages = serviceBillingModel.oneTimeInStages(service.billing);
  if (!oneTimeInStages) return [];
  const errors = [];
  const discount = serviceModel.discount(service);
  if (discount && discount.isValue()) errors.push(OneTimeInStagesError.NonPercentageDiscount);
  if (oneTimeInStages.hasInvalidStages(service.cost)) errors.push(OneTimeInStagesError.InvalidStages);
  return errors;
}